import * as React from 'react';
import { assignedCallMapRecipe } from '../recipes/mobile.recipe';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
} from './BottomSheet';
import { Button } from './Button';
import { Stack } from './Layout';

export interface AssignedCallPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** How far, already formatted: "3.0 miles". Said under the heading. */
  distance?: string;
  /** A small map of the pickup: the app's map, which the library does not load. */
  map?: React.ReactNode;
  /**
   * What the call is, as DetailFields: Type, Priority, Pickup Location with
   * directions, Customer, Dispatched.
   */
  children?: React.ReactNode;
  /** Seen it; it stays on the calls list for later. Closes the prompt. */
  onOkay?: () => void;
  /** Start the call now: the app moves it on and opens it. */
  onStart: () => void;
  /** The start is being saved: its button spins. */
  starting?: boolean;
}

/**
 * The prompt a driver sees when a call is assigned to them, as the Figma's
 * Modal draws it: "You've been assigned a call", how far away it is, a
 * small map of the pickup and what the call is, then Okay or Start Call.
 *
 *   <AssignedCallPrompt open={!!assigned} onOpenChange={dismiss}
 *     distance="3.0 miles" map={<CallsMap … />}
 *     onStart={() => start(call.id)} starting={isStarting}>
 *     <DetailField label="Pickup Location" value={call.pickupLocation} />
 *   </AssignedCallPrompt>
 *
 * It has to be answered: tapping outside or swiping does not put it away,
 * because a call that slides off unseen is a customer waiting. Accept and
 * decline are not here; a driver's call is assigned, not offered.
 */
export function AssignedCallPrompt({
  open,
  onOpenChange,
  distance,
  map,
  children,
  onOkay,
  onStart,
  starting = false,
}: AssignedCallPromptProps) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} dismissible={false}>
      <BottomSheetContent
        title="You’ve been assigned a call"
        description={distance ? `It’s ${distance} away from you` : undefined}
        prominent
        footer={
          <>
            <BottomSheetClose asChild>
              <Button
                variant="secondary"
                size="xl"
                pill
                onClick={onOkay}
                disabled={starting}
              >
                Okay
              </Button>
            </BottomSheetClose>
            <Button size="xl" pill onClick={onStart} loading={starting}>
              Start Call
            </Button>
          </>
        }
      >
        <Stack gap={4}>
          {map ? <div className={assignedCallMapRecipe()}>{map}</div> : null}
          {children}
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
